import React, { useRef, useState, useEffect } from "react";
import { PlayCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Button from "../../components/Button/Button";

function NewHome() {
  const { ref: textRef, inView: textInView } = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const { ref: videoRefInView, inView: videoInView } = useInView({
    triggerOnce: false,
    threshold: 0.5,
  });

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Play video on click
  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  // Pause when scrolled out of view
  useEffect(() => {
    if (videoRef.current) {
      if (!videoInView) {
        videoRef.current.pause();
      }
    }
  }, [videoInView]);

  // Listen to play/pause events to toggle overlay
  useEffect(() => {
    const videoEl = videoRef.current;

    if (!videoEl) return;

    const handlePlayEvent = () => setIsPlaying(true);
    const handlePauseEvent = () => setIsPlaying(false);

    videoEl.addEventListener("play", handlePlayEvent);
    videoEl.addEventListener("pause", handlePauseEvent);

    return () => {
      videoEl.removeEventListener("play", handlePlayEvent);
      videoEl.removeEventListener("pause", handlePauseEvent);
    };
  }, []);

  return (
    <div className="relative bg-[#f9f5ff] overflow-hidden min-h-screen flex flex-col items-center justify-center px-6 pt-32 md:pt-32 pb-8">
      {/* Content */}
      <div className="relative z-10 text-center" ref={textRef}>
        <motion.h1
          initial={{ y: -50, opacity: 0 }}
          animate={textInView ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-4xl md:text-6xl font-bold text-[#42307d] leading-tight"
        >
          Transform Customers into <br /> Raving Fans
        </motion.h1>

        <motion.p
          initial={{ y: -50, opacity: 0 }}
          animate={textInView ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          className="mt-6 text-lg text-[#6941c6] max-w-5xl mx-auto"
        >
          Maximize customer lifetime value with personalized experiences,
          loyalty programs, and performance tracking. Get more 5-star reviews
          and drive business growth.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={textInView ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
          className="mt-8 flex justify-center gap-4"
        >
          <Button variant="primary">Get Started</Button>
          <Button variant="secondary" calendly>
            Book a demo
          </Button>
        </motion.div>

        {/* Video with overlay */}
        <div className="flex mt-4 justify-center">
          <motion.div
            ref={videoRefInView}
            animate={{
              scale: videoInView ? 1 : 0.8,
              opacity: videoInView ? 1 : 0.7,
            }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="relative group w-full max-w-5xl"
          >
            <video
              ref={videoRef}
              src="https://zolaxbucket.s3.eu-north-1.amazonaws.com/zola-files/zola-1.mp4"
              poster="https://cdn.prod.website-files.com/67f50baea549023b9a732b20/67f51194a286c35d8311fd2c_image%2078%20(1).png"
              controls
              className="rounded-xl shadow-lg w-full object-cover"
            />

            {/* Play Button Overlay */}
            {!isPlaying && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                onClick={handlePlay}
              >
                <div className="bg-white/80 hover:bg-white/90 backdrop-blur-md rounded-full p-4 transition cursor-pointer">
                  <PlayCircle className="w-12 h-12 text-[#7f56d9]" />
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default NewHome;
